import type {
  ThinkingItem,
  FileInfo,
  WorkflowTrace,
  ContentBlock
} from './contentBlock';
import type { TopHeaderProps, HeaderCapabilityOption } from '../components/TopHeader';
import type { BottomInputAreaProps } from '../components/BottomInputArea';
import type { ContextResource, HistoryGroup } from '../pages/AiChatPage/RightSidebar';
import type { AiMessageRecord } from './aiHistory';
import type { CodeSelection } from '../../AIPlcCoderPlugin/types';

// ─── 聊天消息 ─────────────────────

export interface ChatMessage {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly content: string;
  readonly dateCreated: string;
  readonly status?: 'thinking' | 'done' | 'error';
  /** 思考/推理过程 */
  readonly thinkingItems?: readonly ThinkingItem[];
  /** 文件列表 */
  readonly files?: readonly FileInfo[];
  /** 工作流追踪 */
  readonly workflowTrace?: WorkflowTrace | null;
  /** @deprecated 旧字段，兼容历史数据 */
  readonly contentBlocks?: readonly ContentBlock[];
}

export type ViewMode = 'home' | 'chat';

// ─── Props ─────────────────────

export interface AiChatPageProps extends TopHeaderProps, BottomInputAreaProps {
  readonly businessType?: string;
  readonly businessId?: string | number | null;
  readonly defaultConversationId?: number;
  readonly defaultCapabilityCode?: string;
  readonly capabilityDisabled?: boolean;
  readonly contextResources?: readonly ContextResource[];
  readonly historyGroups?: readonly HistoryGroup[];
  readonly onClickNewChat?: () => void;
  readonly onSelectConversation?: (id: string) => void;
  readonly onSelectResource?: (id: string) => void;
  readonly onClickShare?: () => void;
  readonly onMessageReceived?: (message: string) => void;
  readonly sidebarMode?: 'fixed' | 'drawer';
  readonly showContextResources?: boolean;
  readonly codeSelection?: CodeSelection | null;
  readonly onClearCodeSelection?: () => void;
  readonly onBeforeSend?: (text: string) => string;
}

export interface AiChatPageRef {
  resetConversation: () => string;
}

// ─── 工具函数 ─────────────────────

export const createId = (): string =>
  `${Date.now()}_${Math.random().toString(16).slice(2)}`;

export const nowIso = (): string => new Date().toISOString();

// ─── DB 数据解析工具 ─────────────────────

/**
 * @deprecated 仅用于兼容旧数据库记录中的 content_blocks 字段
 */
export const parseContentBlocks = (
  raw: readonly Record<string, unknown>[] | string | null | undefined
): readonly ContentBlock[] | undefined => {
  if (!raw) return undefined;
  try {
    const arr: readonly Record<string, unknown>[] =
      typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(arr) || arr.length === 0) return undefined;
    return arr.filter(
      (item): item is ContentBlock =>
        typeof item === 'object' && item !== null && typeof item.type === 'string'
    ) as readonly ContentBlock[];
  } catch {
    return undefined;
  }
};

/**
 * 安全解析 JSON 字段（Directus 可能返回字符串或已解析的对象）
 */
export const safeParseJson = <T,>(raw: unknown): T | undefined => {
  if (!raw) return undefined;
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : (raw as T);
  } catch {
    return undefined;
  }
};

/**
 * 将 DB 消息记录映射为 UI 消息
 *
 * ★ 优先读取新字段（thinking/files/workflow_trace），旧字段 content_blocks 作兜底
 */
export const mapDbMessagesToUi = (rows: readonly AiMessageRecord[]): readonly ChatMessage[] => {
  return rows
    .filter(r => r.role === 'user' || r.role === 'assistant')
    .map(r => ({
      id: `dbmsg_${r.id}`,
      role: r.role as 'user' | 'assistant',
      content: r.content ?? '',
      dateCreated: r.date_created,
      status: 'done' as const,
      thinkingItems: safeParseJson<readonly ThinkingItem[]>((r as any).thinking),
      files: safeParseJson<readonly FileInfo[]>((r as any).files),
      workflowTrace: safeParseJson<WorkflowTrace>((r as any).workflow_trace),
      contentBlocks: parseContentBlocks((r as any).content_blocks)
    }));
};